import { useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import { FETCH_SITE_VIEWING_ROOMS } from "../../../../graphql/query/ViewingRoom";
import { FETCH_SITE_LOCATIONS } from "../../../../graphql/query/WarehouseLocation";
import { FETCH_WAREHOUSE_LOCATION_TYPES } from "../../../../graphql/query/WarehouseLocationType";
import { FETCH_WAREHOUSE_SITES } from "../../../../graphql/query/WarehouseSite";
import useRefetchWhenVisible from "../../../../hooks/useRefetchWhenVisible";
import LocationsPanel from "./components/LocationsPanel";
import SiteHeader from "./components/SiteHeader";
import ViewingRoomsPanel from "./components/ViewingRoomsPanel";
import { hasSiteId, parseSiteId } from "./helpers";

const EMPTY_LIST = [];
const SITE_QUERY_NAMES = [
  "FetchWarehouseSites",
  "FetchWarehouseLocationTypes",
  "FetchSiteViewingRooms",
  "FetchSiteLocations",
];

const SiteManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const siteId = parseSiteId(searchParams.get("siteId"));
  useRefetchWhenVisible({ queryNames: SITE_QUERY_NAMES });

  const { data: sitesData, loading: sitesLoading } = useQuery(
    FETCH_WAREHOUSE_SITES,
  );
  const { data: typesData } = useQuery(
    FETCH_WAREHOUSE_LOCATION_TYPES,
    { variables: { input: { show: "all" } } },
  );
  const { data: allRoomsData, loading: roomsLoading } = useQuery(
    FETCH_SITE_VIEWING_ROOMS,
    { variables: { input: { show: "all" } } },
  );
  const { data: locationsData, loading: locationsLoading } = useQuery(
    FETCH_SITE_LOCATIONS,
    {
      skip: !hasSiteId(siteId),
      variables: { input: { siteId: { operator: "eq", value: siteId } } },
    },
  );

  const warehouseSites = sitesData?.warehouseSites || EMPTY_LIST;
  const locationTypes = typesData?.warehouseLocationTypes || EMPTY_LIST;
  const allViewingRooms = allRoomsData?.viewingRooms || EMPTY_LIST;
  const locations = locationsData?.warehouseLocations || EMPTY_LIST;
  const site = warehouseSites.find(warehouseSite => warehouseSite.id === siteId);
  const siteViewingRooms = allViewingRooms.filter(
    viewingRoom => viewingRoom.siteId === siteId,
  );

  const onSiteChange = nextSiteId =>
    setSearchParams(
      hasSiteId(nextSiteId) ? { siteId: String(nextSiteId) } : {},
    );

  return (
    <div className="w-full px-4 py-8 sm:px-6 lg:px-8">
      <SiteHeader
        loading={sitesLoading}
        site={site}
        siteId={siteId}
        warehouseSites={warehouseSites}
        onSiteChange={onSiteChange}
      />
      {hasSiteId(siteId) ? (
        <>
          <LocationsPanel
            loading={locationsLoading}
            locationTypes={locationTypes}
            locations={locations}
            siteId={siteId}
            viewingRooms={allViewingRooms}
          />
          <ViewingRoomsPanel
            allViewingRooms={allViewingRooms}
            loading={roomsLoading}
            locations={locations}
            site={site}
            siteId={siteId}
            viewingRooms={siteViewingRooms}
            warehouseSites={warehouseSites}
          />
        </>
      ) : (
        <div className="py-12 text-center text-gray-500">
          Choose a site to manage its locations and viewing rooms.
        </div>
      )}
    </div>
  );
};

export default SiteManagement;
